import axios from 'axios';
import { useUserStore } from '@/stores/user/userStore';
import { useAppStore } from '@/stores/app/appStore';

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  withCredentials: true, // Důležité pro posílání cookies (refresh token)
});

// Přidání interceptoru pro autorizaci
axiosInstance.interceptors.request.use((config) => {
  const userStore = useUserStore();
  const token = userStore.token;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

let isRefreshing = false;
let failedQueue: any[] = [];

function safeParse(s: string): any {
  try { return JSON.parse(s); } catch { return null; }
}

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Interceptor pro odpovědi
axiosInstance.interceptors.response.use((response) => {
  // ── Phone prompt: trigger after successful event registration when user has no phone ──
  try {
    const cfg = response.config;
    const url = cfg?.url || '';
    const method = (cfg?.method || '').toLowerCase();
    // Match POST /events/<id>/register/player[/<side>] (covers players, goalies and both home/away sides)
    if (
      method === 'post'
      && /\/events\/[^/]+\/register\/player(\/\w+)?$/.test(url)
    ) {
      const userStore = useUserStore();
      const me = userStore.user as any;
      const playerArg = (cfg as any)?.data
        ? (typeof (cfg as any).data === 'string' ? safeParse((cfg as any).data) : (cfg as any).data)
        : null;
      const targetId = playerArg?.player?.id;
      // Only prompt when the registered user is the logged-in user themselves
      // (so coaches assigning other players don't get hassled).
      const isSelf = !targetId || (me?._id && String(targetId) === String(me._id));
      const hasPhone = !!(me?.phone && String(me.phone).replace(/\D/g, '').length >= 6);
      if (isSelf && me && !hasPhone) {
        // Lazy import to avoid circular deps at module load
        import('@/stores/phonePrompt/phonePromptStore').then(({ usePhonePromptStore }) => {
          const store = usePhonePromptStore();
          // Use eventId from URL as source — prevents repeat-prompt when toggling registration on the same event.
          const m = url.match(/\/events\/([^/]+)\/register\/player/);
          const eventId = m?.[1];
          store.open(eventId);
        }).catch(() => { /* ignore */ });
      }
    }
  } catch { /* never break the response chain */ }
  return response;
}, async (error) => {
  const originalRequest = error.config;

  // Jen pokud má uživatel token a ještě jsme to nezkusili refreshnout
  const userStore = useUserStore();
  if (error.response?.status === 401 && !originalRequest._retry && userStore.token) {
    if (isRefreshing) {
      return new Promise(function(resolve, reject) {
        failedQueue.push({resolve, reject});
      }).then(token => {
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        return axiosInstance(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Volání refresh endpointu
      const response = await axios.post(`${axiosInstance.defaults.baseURL}/auth/refresh-token`, {}, {
          withCredentials: true
      });

      const { token } = response.data;
      
      const userStore = useUserStore();
      userStore.setToken(token);
      
      originalRequest.headers['Authorization'] = 'Bearer ' + token;
      
      processQueue(null, token);
      isRefreshing = false;
      
      return axiosInstance(originalRequest);
    } catch (err) {
      processQueue(err, null);
      isRefreshing = false;
      localStorage.removeItem('token');
      const userStore = useUserStore();
      userStore.clearUser();
      // Redirect na /login pouze pokud tam ještě nejsme (prevence redirect loop)
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(err);
    }
  }

  return Promise.reject(error);
});

export default axiosInstance;
